import { Check, Clock, Info, MapPin, Mic, ScanQrCode } from 'lucide-react'
import type { Slot } from '../lib/types'
import {
  canCheckIn, checkInHint, formatClock, formatDay, slotState, slotWindow, splitSpeaker, splitTitle,
} from '../lib/slot-time'
import { Button } from '../ui/Button'
import { Sheet } from '../ui/Sheet'

interface SlotDetailProps {
  slot: Slot | null
  now: number
  onClose: () => void
  onCheckIn: (slot: Slot) => void
}

export function SlotDetail({ slot, now, onClose, onCheckIn }: SlotDetailProps) {
  if (!slot) return null

  const { label, title } = splitTitle(slot.slot_name)
  const { start, end } = slotWindow(slot)
  const state = slotState(slot, now)
  const attended = Boolean(slot.checkin_status)
  const open = canCheckIn(slot, now)
  const hint = checkInHint(slot, now)
  const extended = Number(slot.extended_time) || 0

  return (
    <Sheet open onClose={onClose} title={title}>
      {label ? <p className="kicker detail__label">{label}</p> : null}
      <h2 className="detail__title">{title}</h2>

      <dl className="detail__facts">
        <div className="detail__fact">
          <dt>Time</dt>
          <dd>
            <span className="tnum">
              {formatClock(start)} – {formatClock(end)}
            </span>
            <br />
            <span className="faint">{formatDay(start)}</span>
            {extended > 0 ? (
              <>
                <br />
                <span className="faint">Includes {extended} min extension</span>
              </>
            ) : null}
          </dd>
        </div>

        <div className="detail__fact">
          <dt>Venue</dt>
          <dd>
            <MapPin
              size={14}
              strokeWidth={1.5}
              style={{ display: 'inline', verticalAlign: '-2px', marginRight: 6 }}
            />
            {slot.venue_name}
          </dd>
        </div>

        {slot.speaker ? (
          <div className="detail__fact">
            <dt>Speaker</dt>
            <dd>
              <Mic
                size={14}
                strokeWidth={1.5}
                style={{ display: 'inline', verticalAlign: '-2px', marginRight: 6 }}
              />
              {splitSpeaker(slot.speaker).name}
              {splitSpeaker(slot.speaker).position ? (
                <span className="detail__speaker-position">{splitSpeaker(slot.speaker).position}</span>
              ) : null}
            </dd>
          </div>
        ) : null}

        <div className="detail__fact">
          <dt>Attendance</dt>
          <dd>
            {attended ? (
              <span style={{ color: 'var(--ok)' }}>
                <Check
                  size={14}
                  strokeWidth={2.5}
                  style={{ display: 'inline', verticalAlign: '-2px', marginRight: 6 }}
                />
                Recorded
              </span>
            ) : !slot.qr_required ? (
              'Not counted towards your certificate'
            ) : state === 'past' ? (
              'Missed'
            ) : (
              'Scan the QR code in the room'
            )}
          </dd>
        </div>
      </dl>

      {attended ? null : !slot.qr_required ? (
        <p className="detail__hint">
          <Info size={14} strokeWidth={1.5} />
          No check-in needed for this one.
        </p>
      ) : open ? (
        <Button
          variant="primary"
          icon={<ScanQrCode size={17} strokeWidth={1.5} />}
          onClick={() => onCheckIn(slot)}
        >
          Scan to check in
        </Button>
      ) : (
        <>
          <Button variant="outline" disabled>
            Check-in unavailable
          </Button>
          {hint ? (
            <p className="detail__hint">
              <Clock size={14} strokeWidth={1.5} />
              {hint}
            </p>
          ) : null}
        </>
      )}
    </Sheet>
  )
}
